import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { scrollToId, scrollToTop } from "@/lib/scroll";

const MENUS = [
  { label: "Beranda", path: "/" },
  {
    label: "LLDIKTI Wilayah VII",
    items: [
      {
        label: "Laman LLDIKTI Wilayah VII",
        href: "https://lldikti7.kemdiktisaintek.go.id/",
      },
      { label: "Pejabat Struktural", path: "/pejabat-struktural" },
      {
        label: "Tugas dan Fungsi",
        href: "https://lldikti7.kemdiktisaintek.go.id/profil/tupoksi",
      },
      {
        label: "Visi dan Misi",
        href: "https://lldikti7.kemdiktisaintek.go.id/profil/visi-misi",
      },
      {
        label: "Rencana Strategi",
        href: "https://lldikti7.kemdiktisaintek.go.id/kinerja/rencana-strategi",
      },
      {
        label: "Perjanjian Kinerja",
        href: "https://lldikti7.kemdiktisaintek.go.id/kinerja/perjanjian-kinerja",
      },
      {
        label: "Indeks Kepuasan Masyarakat",
        path: "/indeks-kepuasan-masyarakat",
      },
      { label: "LHKPN LLDIKTI VII", path: "/lhkpn" },
      { label: "Pengumuman", path: "/pengumuman" },
    ],
  },
  { label: "Profil PPID", path: "/profil-ppid" },
  {
    label: "Layanan Informasi",
    items: [
      { label: "Maklumat Pelayanan", path: "/", anchor: "#maklumat-pelayanan" },
      { label: "Standar Pelayanan", path: "/standar-pelayanan" },
      { label: "Daftar Informasi Publik", path: "/daftar-informasi-publik" },
      {
        label: "Pengajuan Informasi Publik",
        items: [
          {
            label: "Mengajukan Permohonan Informasi",
            path: "/pengajuan-informasi",
          },
          { label: "Pengajuan Keberatan", path: "/pengajuan-keberatan" },
          { label: "Penyelesaian Sengketa", path: "/penyelesaian-sengketa" },
        ],
      },
      { label: "Saluran Pengaduan", path: "/saluran-pengaduan" },
    ],
  },
  {
    label: "Data dan Laporan",
    items: [
      {
        label: "Laporan Kinerja LLDIKTI VII",
        href: "https://lldikti7.kemdiktisaintek.go.id/kinerja/laporan-kinerja",
      },
      { label: "Pengelolaan Program dan Anggaran", path: "/pengelolaan-program-dan-anggaran" },
      { label: "Data Pegawai", path: "/pengelolaan-kepegawaian" },
      { label: "Sarana dan Prasarana", path: "/sarana-dan-prasarana" },
      { label: "Dosen", path: "/dosen-dan-tendik" },
      { label: "Kelembagaan", path: "/kelembagaan" },
      { label: "Akademik", path: "/akademik" },
      { label: "Kemahasiswaan", path: "/kemahasiswaan" },
    ],
  },
  { label: "Kontak", target: "#kontak" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function NestedItems({ items, onOpen }) {
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.22 }}
      className="overflow-hidden"
    >
      {items.map((sub) => (
        <button
          key={sub.label}
          data-testid={`menu-item-${slug(sub.label)}`}
          onClick={() => onOpen(sub)}
          className="block w-full rounded-xl px-4 py-2.5 pl-8 text-left text-[13px] font-semibold text-[#1479BF] transition-colors duration-200 hover:bg-[#EFF6FC]"
        >
          {sub.label}
        </button>
      ))}
    </motion.div>
  );
}

export default function Navbar() {
  const [openMenu, setOpenMenu] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);
  const navigate = useNavigate();

  const go = (target) => {
    setOpenMenu(null);
    setOpenSub(null);
    setMobileOpen(false);
    setMobileSub(null);
    scrollToId(target);
  };

  const openItem = (item) => {
    setOpenMenu(null);
    setOpenSub(null);
    setMobileOpen(false);
    setMobileSub(null);
    if (item.path && item.anchor) {
      navigate(item.path);
      setTimeout(() => scrollToId(item.anchor), 700);
    } else if (item.path) navigate(item.path);
    else if (item.href)
      window.open(item.href, "_blank", "noopener,noreferrer");
    else toast(`${item.label} — halaman sedang dalam pengembangan.`);
  };

  const toggleMenu = (label) => {
    setOpenSub(null);
    setOpenMenu(openMenu === label ? null : label);
  };

  return (
    <>
      {openMenu && (
        <div
          data-testid="menu-overlay"
          onClick={() => {
            setOpenMenu(null);
            setOpenSub(null);
          }}
          className="fixed inset-0 z-40"
        />
      )}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-[#E3EBF3] bg-white/85 backdrop-blur-xl">
        <div className="flex h-16 items-center justify-between gap-4 px-4 md:px-8">
          <button
            data-testid="navbar-home"
            onClick={() => {
              setMobileOpen(false);
              setMobileSub(null);
              setOpenMenu(null);
              setOpenSub(null);
              navigate("/");
              scrollToTop();
            }}
            className="flex items-center gap-3 rounded-2xl text-left transition-colors duration-300 hover:bg-[#F3F7FB]"
          >
            <img
              src="/logo.webp"
              alt="Logo Tut Wuri Handayani — Kemdiktisaintek"
              className="h-10 w-10 object-contain"
            />
            <span>
              <span className="block text-[13px] font-bold leading-tight text-[#0B2440]">
                Lembaga Layanan Pendidikan Tinggi Wilayah VII
              </span>
              <span className="mt-0.5 block text-[11px] font-medium leading-tight text-[#1479BF]">
                Pejabat Pengelola Informasi dan Dokumentasi (PPID)
              </span>
            </span>
          </button>

          <nav className="hidden items-center gap-1 lg:flex">
            {MENUS.map((m) => {
              if (m.path) {
                return (
                  <button
                    key={m.label}
                    data-testid={`menu-${slug(m.label)}`}
                    onClick={() => {
                      setMobileOpen(false);
                      setMobileSub(null);
                      setOpenMenu(null);
                      setOpenSub(null);
                      navigate(m.path);
                    }}
                    className="rounded-full px-4 py-2 text-[13px] font-medium text-slate-600 transition-colors duration-300 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                  >
                    {m.label}
                  </button>
                );
              }
              if (m.target) {
                return (
                  <button
                    key={m.label}
                    data-testid={`menu-${slug(m.label)}`}
                    onClick={() => go(m.target)}
                    className="rounded-full px-4 py-2 text-[13px] font-medium text-slate-600 transition-colors duration-300 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                  >
                    {m.label}
                  </button>
                );
              }
              const open = openMenu === m.label;
              return (
                <div key={m.label} className="relative">
                  <button
                    data-testid={`menu-${slug(m.label)}`}
                    aria-expanded={open}
                    onClick={() => toggleMenu(m.label)}
                    className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-medium transition-colors duration-300 ${open ? "bg-[#EFF6FC] text-[#1479BF]" : "text-slate-600 hover:bg-[#EFF6FC] hover:text-[#1479BF]"}`}
                  >
                    {m.label}
                    <span
                      aria-hidden="true"
                      className={`inline-block text-[10px] transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                    >
                      ▾
                    </span>
                  </button>
                  <AnimatePresence>
                    {open && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-0 top-full w-72 rounded-2xl border border-[#E3EBF3] bg-white p-2 shadow-[0_18px_44px_rgba(11,36,64,0.12)]"
                      >
                        {m.items.map((it) =>
                          it.items ? (
                            <div key={it.label}>
                              <button
                                data-testid={`menu-item-${slug(it.label)}`}
                                aria-expanded={openSub === it.label}
                                onClick={() =>
                                  setOpenSub(
                                    openSub === it.label ? null : it.label,
                                  )
                                }
                                className="flex w-full items-center justify-between gap-2 rounded-xl px-4 py-2.5 text-left text-[13px] font-semibold text-[#1479BF] transition-colors duration-200 hover:bg-[#EFF6FC]"
                              >
                                {it.label}
                                <span
                                  aria-hidden="true"
                                  className={`inline-block text-[10px] transition-transform duration-300 ${openSub === it.label ? "rotate-180" : ""}`}
                                >
                                  ▾
                                </span>
                              </button>
                              <AnimatePresence initial={false}>
                                {openSub === it.label && (
                                  <NestedItems
                                    items={it.items}
                                    onOpen={openItem}
                                  />
                                )}
                              </AnimatePresence>
                            </div>
                          ) : (
                            <button
                              key={it.label}
                              data-testid={`menu-item-${slug(it.label)}`}
                              onClick={() => openItem(it)}
                              className={`block w-full rounded-xl px-4 py-2.5 text-left text-[13px] transition-colors duration-200 hover:bg-[#EFF6FC] hover:text-[#1479BF] ${it.path || it.href ? "font-semibold text-[#1479BF]" : "text-slate-700"}`}
                            >
                              {it.label}
                            </button>
                          ),
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Buka menu"
            aria-expanded={mobileOpen}
            onClick={() => {
              setMobileOpen(!mobileOpen);
              setMobileSub(null);
            }}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E3EBF3] text-[#0B2440] transition-colors duration-300 hover:bg-[#EFF6FC] lg:hidden"
          >
            {mobileOpen ? "✕" : "≡"}
          </button>
        </div>

        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="absolute inset-x-0 top-full max-h-[calc(100vh-4rem)] overflow-y-auto rounded-b-3xl border-b border-[#E3EBF3] bg-white shadow-[0_24px_48px_rgba(11,36,64,0.12)] lg:hidden"
          >
            <div className="space-y-6 px-5 py-6">
              {MENUS.map((m) => {
                if (m.path || m.target) {
                  return (
                    <button
                      key={m.label}
                      data-testid={`mobile-menu-${slug(m.label)}`}
                      onClick={() => (m.path ? openItem(m) : go(m.target))}
                      className="block w-full border-b border-[#E3EBF3] pb-3 text-left text-[13px] font-semibold text-slate-700"
                    >
                      {m.label}
                    </button>
                  );
                }
                return (
                  <div key={m.label}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#1479BF]">
                      {m.label}
                    </p>
                    <ul className="mt-2">
                      {m.items.map((it) =>
                        it.items ? (
                          <li key={it.label}>
                            <button
                              data-testid={`mobile-menu-item-${slug(it.label)}`}
                              aria-expanded={mobileSub === it.label}
                              onClick={() =>
                                setMobileSub(
                                  mobileSub === it.label ? null : it.label,
                                )
                              }
                              className="flex w-full items-center justify-between border-b border-[#E3EBF3] py-2.5 text-left text-sm font-semibold text-[#1479BF]"
                            >
                              {it.label}
                              <span
                                aria-hidden="true"
                                className={`inline-block text-[10px] transition-transform duration-300 ${mobileSub === it.label ? "rotate-180" : ""}`}
                              >
                                ▾
                              </span>
                            </button>
                            <AnimatePresence initial={false}>
                              {mobileSub === it.label && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.22 }}
                                  className="overflow-hidden"
                                >
                                  {it.items.map((sub) => (
                                    <button
                                      key={sub.label}
                                      data-testid={`mobile-menu-item-${slug(sub.label)}`}
                                      onClick={() => openItem(sub)}
                                      className="block w-full border-b border-[#E3EBF3] py-2.5 pl-4 pr-2 text-left text-sm text-slate-700"
                                    >
                                      {sub.label}
                                    </button>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </li>
                        ) : (
                          <li key={it.label}>
                            <button
                              data-testid={`mobile-menu-item-${slug(it.label)}`}
                              onClick={() => openItem(it)}
                              className="block w-full border-b border-[#E3EBF3] py-2.5 text-left text-sm text-slate-700"
                            >
                              {it.label}
                            </button>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
